class VerifyProfileManager {
  constructor() {
    this.elements = {};

    this.config = {
      exceptionMessage: window.errorMessage,
      verifyOtpHash: '#verify-otp',
      selectMethodHash: '#select-method',
      maxCodeLength: {
        email: 6,
        sms: 4,
      },
    };

    this.methodSelectionLoading = false;
    this.dynamicTimer = null;
  }

  /**
   * Initialize the verification manager
   * Includes flow control, session validation, and event bindings
   */
  init() {
    this.cacheElements();
    this.setupInitialState();
    this.bindEvents();
    this.handleHashChange();

    // Initialize flow state if not already set
    if (getAuthFlowState() === AUTH_FLOW_STATES.INITIAL) {
      setAuthFlowState(AUTH_FLOW_STATES.INITIAL);
    }
  }

  // Cache DOM elements for better performance
  cacheElements() {
    this.elements.verifyProfileForm = document.getElementById('verifyProfileForm');
    this.elements.emailInput = this.elements.verifyProfileForm?.querySelector('#email');
    this.elements.benefitsMessageBox = this.elements.verifyProfileForm?.querySelector('#benefitsMessageBox');
    this.elements.verifySubmitButton = this.elements.verifyProfileForm?.querySelector('#verifyProfileSubmit');
    this.elements.loginWelcomeMessage = document.getElementById('loginWelcomeMessage');

    this.elements.emailVerificationCodeForm = document.getElementById('emailVerificationCodeForm');
    this.elements.verificationCodeInput = this.elements.emailVerificationCodeForm?.querySelector('#verificationCode');
    this.elements.verificationSubmitButton = this.elements.emailVerificationCodeForm?.querySelector('#submitButton');
    this.elements.resendButton = this.elements.emailVerificationCodeForm?.querySelector('#resendVerificationCode');
    this.elements.timerElement = this.elements.emailVerificationCodeForm?.querySelector('#dynamic-timer');
  }

  /**
   * Setup initial state based on checkout status
   * Handles direct access to verify-email hash on page load
   */
  setupInitialState() {
    this.isCheckout = getSessionStorageItem(SESSION_KEY.CHECKOUT);
    this.returnTo = getSessionStorageItem(SESSION_KEY.RETURN_TO);

    // Handle checkout-specific UI changes based on current screen
    if (this.isCheckout) {
      this.handleCheckoutUI();
    }

    // Check for direct access to verify-email hash on page load
    if (window.location.hash === this.config.selectMethodHash || window.location.hash === this.config.verifyOtpHash) {
      if (!canAccessVerifyEmail()) {
        console.warn('Direct access to verify otp page not allowed. Redirecting to login.');
        window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
        return;
      }
    }
  }

  /**
   * Ensure valid email flow state
   * Redirects to login if email is missing or flow state is invalid
   * @param {string} emailFromSession - Email from session storage
   */
  ensureValidEmailFlow(emailFromSession) {
    if (!emailFromSession || !canAccessVerifyEmail()) {
      console.warn('Invalid state: cannot resend verification code.');
      redirectToLogin();
      return;
    }
  }

  // Initialize the dynamic timer for OTP resend
  initDynamicTimer() {
    this.dynamicTimer = new DynamicTimer({
      duration: 60,
      elementId: 'dynamic-timer',
      persistKey: 'otp_timer',
      disableSelector: '#resendVerificationCode',
      onComplete: () => {},
    });

    this.dynamicTimer.start();
  }

  // Reset the dynamic timer when a new verification flow starts
  resetDynamicTimer() {
    if (this.dynamicTimer) {
      this.dynamicTimer.destroy();
      this.dynamicTimer = null;
    }

    // Create a fresh timer instance
    this.initDynamicTimer();
  }

  /**
   * Handle checkout-specific UI changes based on current screen
   * Only hides header block on email entry screen, not on method selection screen
   */
  handleCheckoutUI() {
    if (this.elements.loginWelcomeMessage) {
      this.elements.loginWelcomeMessage.classList.remove('hidden');
    }

    if (this.elements.benefitsMessageBox) {
      this.elements.benefitsMessageBox.classList.remove('hidden');
    }

    document.getElementById('enter-email')?.querySelector('#auth-header-block')?.classList.add('hidden');
  }

  /**
   * Bind event listeners
   * Sets up event listeners for form submissions and input changes
   */
  bindEvents() {
    if (this.elements.emailInput) {
      this.elements.emailInput.addEventListener('input', () => {
        this.elements.verifySubmitButton.disabled = !isEmailValid(this.elements.emailInput.value);
      });
    }

    // Verify profile form submission Email screen
    if (this.elements.verifyProfileForm) {
      this.elements.verifyProfileForm.addEventListener('submit', (event) => this.handleVerifyProfileEmailSubmit(event));
    }

    // Hash change listener
    window.addEventListener('hashchange', () => this.handleHashChange());

    // Method selection click handlers
    document.addEventListener('click', (event) => {
      if (event.target.closest('#email-method')) {
        event.preventDefault();
        this.handleMethodSelectionClick('email');
      }

      if (event.target.closest('#sms-method')) {
        event.preventDefault();
        this.handleMethodSelectionClick('sms');
      }
    });
  }

  // Handle verify profile form submission
  async handleVerifyProfileEmailSubmit(event) {
    event.preventDefault();

    const requestBody = {
      email: this.elements.emailInput.value.trim(),
    };

    const operation = async () => {
      const response = await this.verifyProfileEmail(requestBody);
      const responseData = await response.json().catch(() => null);

      setSessionStorageItem(SESSION_KEY.PROFILE, {
        email: requestBody.email,
        profile_data: responseData || {},
        statusCode: response.status,
      });

      if (response.ok) {
        if (responseData?.email && responseData?.phoneNumber) {
          window.location.hash = this.config.selectMethodHash;
        } else if (responseData?.email && !responseData?.phoneNumber) {
          await this.sendEmailVerificationFlow(requestBody, 'email');
        }

        setAuthFlowState(AUTH_FLOW_STATES.EMAIL_SUBMITTED);
      } else if (
        response.status === VERIFICATION_CODE.PROFILE.NOT_FOUND ||
        response.status === VERIFICATION_CODE.PROFILE.ALREADY_EXISTS
      ) {
        // Email verification flow
        await this.sendEmailVerificationFlow(requestBody, 'email');
        setAuthFlowState(AUTH_FLOW_STATES.EMAIL_SUBMITTED);
      } else {
        this.handleError(response.status);
      }
    };

    await this.executeWithErrorHandling(operation, this.elements.verifySubmitButton);
  }

  /**
   * Send email verification flow
   * @param {Object} requestBody - Request body containing email
   * @param {string} method - Verification method ('email' or 'sms') : default 'email'
   */
  async sendEmailVerificationFlow(requestBody, method = 'email') {
    try {
      const body = { ...requestBody, type: method === 'email' ? 'EMAIL' : 'PHONE' };

      setSessionStorageItem(SESSION_KEY.VERIFICATION_METHOD, method);
      const resendResult = await this.resendVerificationCode(body);

      if (!resendResult.ok) {
        this.handleError(resendResult.status);
        return;
      }

      window.location.hash = this.config.verifyOtpHash;
    } catch (error) {
      this.handleError(error.status);
    }
  }

  /**
   * Execute async operation with standardized error handling and loading states
   * Reduces duplicated try/catch patterns and button state management
   * @param {Function} operation - Async operation to execute
   * @param {HTMLElement} button - Button to show loading state
   * @param {boolean} useFullScreenLoader - Whether to use full screen loader
   * @param {string} successMessage - Optional success message
   */
  async executeWithErrorHandling(operation, button = null, useFullScreenLoader = false, successMessage = null) {
    try {
      if (button) setButtonLoading(button, true);
      if (useFullScreenLoader) toggleFullScreenPreloader(true);

      const result = await operation();
      if (successMessage) showToastMessage(successMessage);

      return result;
    } catch (error) {
      showToastMessage(this.config.exceptionMessage.somethingWentWrong);
      throw error;
    } finally {
      if (button) setButtonLoading(button, false);
      if (useFullScreenLoader) toggleFullScreenPreloader(false);
    }
  }

  /**
   * Determine verification type based on profile data
   * @returns {string} - 'email' or 'sms'
   */
  determineVerificationType() {
    const storedMethod = getSessionStorageItem(SESSION_KEY.VERIFICATION_METHOD);
    if (storedMethod) return storedMethod;

    return 'email';
  }

  /**
   * Get the required OTP length for the current verification method
   * @returns {number} - Required OTP length (4 for SMS, 6 for email)
   */
  getCurrentOtpLength() {
    const verificationType = this.determineVerificationType();

    return this.config.maxCodeLength[verificationType] || this.config.maxCodeLength.email;
  }

  /**
   * Update verification screen content based on selected method
   * @param {string} method - 'email' or 'sms'
   * @param {string} email - User's email address
   * @param {Object} profileData - Profile data containing phone information
   */
  updateVerificationScreen(method, profileData) {
    const profileSession = getSessionStorageItem(SESSION_KEY.PROFILE) || {};
    const statusCode = profileSession?.statusCode;
    const emailFromSession = profileSession?.email;

    const verificationForm = this.elements.emailVerificationCodeForm;
    if (!verificationForm) return;

    // Update title and description based on method
    const titleElement = verificationForm.closest('#verify-otp')?.querySelector('.title'); // auth-header-block title (h1)
    const descriptionElement = verificationForm.closest('#verify-otp')?.querySelector('.description');

    if (method === 'email') {
      const profileStatuses = [VERIFICATION_CODE.PROFILE.NOT_FOUND, VERIFICATION_CODE.PROFILE.ALREADY_EXISTS];

      if (profileStatuses.includes(statusCode)) {
        titleElement.textContent = window.authFlowString?.verifyOtp?.emailVerificationNewUser.title;
        const rowDescription = window.authFlowString.verifyOtp.emailVerificationNewUser.description;
        const email = profileData?.email || profileData?.data.email || emailFromSession || '';

        descriptionElement.innerHTML = rowDescription.replace(
          "<span class='email_address'></span>",
          `<span class='email_address'>${email}</span>`
        );
      } else {
        titleElement.textContent = window.authFlowString.verifyOtp.emailVerification.title;
        const rowDescription = window.authFlowString.verifyOtp.emailVerification.description;
        const email = profileData?.email || profileData?.data.email || emailFromSession || '';

        descriptionElement.innerHTML = rowDescription.replace(
          "<span class='email_address'></span>",
          `<span class='email_address'>${email}</span>`
        );
      }
    } else if (method === 'sms') {
      titleElement.textContent = window.authFlowString.verifyOtp.smsVerification.title;
      let description = window.authFlowString.verifyOtp.smsVerification.description;
      const phoneNumber = profileData?.phoneNumber || '';

      description = description.replace(
        "<span class='phone_number'></span>",
        `<span class='phone_number'>${phoneNumber}</span>`
      );

      descriptionElement.innerHTML = description;
    }

    // Update input field max length based on method
    const verificationInput = verificationForm.querySelector('#verificationCode');
    if (verificationInput) {
      const maxLength = method === 'sms' ? this.config.maxCodeLength.sms : this.config.maxCodeLength.email;
      verificationInput.setAttribute('maxlength', maxLength);

      // Clear any existing input when switching methods
      verificationInput.value = '';

      // Disable submit button since input is now empty
      const submitButton = verificationForm.querySelector('#submitButton');
      if (submitButton) {
        submitButton.disabled = true;
      }
    }
  }

  /**
   * Handle method selection click with proper validation
   * Simplified approach that gets data when needed
   * @param {string} method - 'email' or 'sms'
   */
  async handleMethodSelectionClick(method) {
    // Prevent multiple simultaneous clicks
    if (this.methodSelectionLoading) {
      return;
    }

    const profileSession = getSessionStorageItem(SESSION_KEY.PROFILE);
    const profileData = profileSession?.profile_data;

    if (!profileData) {
      console.error('Profile data not available for method selection');
      window.location.replace(PAGE_URLS.SHOPIFY_LOGIN);
      return;
    }

    // Set loading state and disable both cards
    this.methodSelectionLoading = true;
    this.setMethodCardsLoadingState(method, true);
    setSessionStorageItem(SESSION_KEY.VERIFICATION_METHOD, method);

    try {
      await this.sendEmailVerificationFlow({ email: profileSession.email }, method);
    } catch (error) {
      console.error('Error during method selection:', error);
      this.setMethodCardsLoadingState(method, false);
      this.methodSelectionLoading = false;
    } finally {
      this.methodSelectionLoading = false;
      this.setMethodCardsLoadingState(method, false);
    }
  }

  /**
   * Set loading state for method selection cards
   * @param {string} activeMethod - The method being processed ('email' or 'sms')
   * @param {boolean} isLoading - Whether to show loading state
   */
  setMethodCardsLoadingState(activeMethod, isLoading) {
    const emailCard = document.getElementById('email-method');
    const smsCard = document.getElementById('sms-method');
    const activeCard = document.getElementById(`${activeMethod}-method`);
    const inactiveCard = activeMethod === 'email' ? smsCard : emailCard;

    if (isLoading) {
      if (activeCard) activeCard.classList.add('active-loading-card');
      if (inactiveCard) inactiveCard.classList.add('inactive-loading-card');

      // Add loading spinner to active card
      if (activeCard && !activeCard.querySelector('#customLoader')) {
        const loaderElement = document.createElement('div');
        loaderElement.setAttribute('id', 'customLoader');
        loaderElement.setAttribute('role', 'status');
        loaderElement.classList.add('absolute', '-translate-x-1/2', '-translate-y-1/2', 'top-2/4', 'left-1/2');

        const svg = `<svg aria-hidden="true" class="w-8 h-8 text-gray-2 animate-spin fill-primary" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/><path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/></svg>
          <span class="sr-only">Loading...</span>`;
        loaderElement.innerHTML = svg;

        activeCard.appendChild(loaderElement);
      }
    } else {
      if (activeCard) {
        activeCard.classList.remove('active-loading-card');
        const loader = activeCard.querySelector('#customLoader');

        if (loader) loader.remove();
      }

      if (inactiveCard) {
        inactiveCard.classList.remove('inactive-loading-card');
        const loader = inactiveCard.querySelector('#customLoader');

        if (loader) loader.remove();
      }
    }
  }

  /**
   * Handle resending verification code
   * Updated for V2 API with proper response handling
   */
  async handleResendVerificationCode() {
    const emailFromSession = getSessionStorageItem(SESSION_KEY.PROFILE)?.email;

    // Validate session email and flow state
    this.ensureValidEmailFlow(emailFromSession);

    const operation = async () => {
      const verificationType = this.determineVerificationType();

      const requestBody = {
        email: emailFromSession,
        type: verificationType === 'email' ? 'EMAIL' : 'PHONE',
      };

      const response = await this.resendVerificationCode(requestBody);
      const responseData = await response.json().catch(() => null);

      if (response.ok) {
        const maskedContact = responseData?.data || '';

        const message = maskedContact
          ? `Verification code sent successfully to ${maskedContact}!`
          : 'Verification code sent successfully!';

        showToastMessage(message);
        return;
      }

      this.handleError(response.status);
    };

    await this.executeWithErrorHandling(
      operation,
      null,
      true // Use full screen loader
    );
  }

  /**
   * Handle hash change event to show appropriate form
   * Improved hash routing with better validation and state restoration
   */
  handleHashChange() {
    const currentHash = window.location.hash;
    const profileSession = getSessionStorageItem(SESSION_KEY.PROFILE);

    if (currentHash === this.config.selectMethodHash || currentHash === this.config.verifyOtpHash) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        });
      });
    }

    // Handle select method hash
    if (currentHash === this.config.selectMethodHash) {
      this.setupMethodSelectionScreen(profileSession);
      return;
    }

    // Handle verify OTP hash
    if (currentHash === this.config.verifyOtpHash) {
      const emailFromSession = profileSession?.email;

      // Validate session email and flow state before proceeding
      this.ensureValidEmailFlow(emailFromSession);

      // Show header block for OTP screen during checkout
      if (this.isCheckout) {
        const authHeaderBlock = document.getElementById('auth-header-block');
        const headerBlock = authHeaderBlock?.querySelector('.header-block');

        if (headerBlock && headerBlock.classList.contains('hidden')) {
          headerBlock.classList.remove('hidden');
        }
      }

      // Setup verification code form
      this.setupVerificationCodeForm();

      // Initialize timer only if it doesn't exist (first time on OTP screen)
      if (!this.dynamicTimer) {
        this.initDynamicTimer();
      }
    }
  }

  /**
   * Setup method selection screen
   * Populates subtext for method selection cards based on profile data
   * @param {Object} profileSession - Profile session from storage
   */
  setupMethodSelectionScreen(profileSession) {
    const profileData = profileSession?.profile_data;

    const emailMethodSubtext = document.getElementById('email_verification');
    if (emailMethodSubtext) {
      emailMethodSubtext.textContent = profileData?.email;
    }

    const smsMethodSubtext = document.getElementById('sms_verification');
    if (smsMethodSubtext) {
      const phoneNumber = profileData?.phoneNumber;
      smsMethodSubtext.textContent = phoneNumber;
    }
  }

  /**
   * Setup verification code form elements and event listeners
   */
  setupVerificationCodeForm() {
    const verificationCodeInput = this.elements.verificationCodeInput;
    const emailVerificationCodeForm = this.elements.emailVerificationCodeForm;
    const resendButton = this.elements.resendButton;
    const verificationSubmitButton = this.elements.verificationSubmitButton;

    if (verificationCodeInput && !verificationCodeInput.hasListener) {
      verificationCodeInput.addEventListener('input', (event) => this.validateVerificationCodeInput(event));
      verificationCodeInput.hasListener = true;
    }

    if (emailVerificationCodeForm && !emailVerificationCodeForm.hasListener) {
      emailVerificationCodeForm.addEventListener('submit', (event) => this.handleEmailVerificationSubmit(event));
      emailVerificationCodeForm.hasListener = true;
    }

    // Setup resend button
    this.setupResendButton(resendButton, emailVerificationCodeForm, verificationSubmitButton);

    // Update verification screen based on stored method or default to email
    const profileSession = getSessionStorageItem(SESSION_KEY.PROFILE);
    if (profileSession) {
      const verificationType = this.determineVerificationType();
      this.updateVerificationScreen(verificationType, profileSession.profile_data);
    }
  }

  /**
   * Setup resend button handler
   * @param {HTMLElement} resendButton - Resend button element
   * @param {HTMLElement} form - Form to reset
   * @param {HTMLElement} submitButton - Submit button to disable
   */
  setupResendButton(resendButton, form, submitButton) {
    if (resendButton && !resendButton.hasListener) {
      resendButton.addEventListener('click', (event) => {
        event.preventDefault();

        // Reset form and disable submit button
        form?.reset();
        if (submitButton) {
          submitButton.disabled = true;
        }

        this.handleResendVerificationCode();
      });

      resendButton.hasListener = true;
    }
  }

  /**
   * Validate verification code input
   * Ensures only numeric input and max length based on verification method
   */
  validateVerificationCodeInput(event) {
    const maxCodeLength = this.getCurrentOtpLength();
    const { verificationSubmitButton } = this.elements;

    // Remove any non-numeric characters
    let numericInput = event.target.value.replace(/[^0-9]/g, '');

    // Restrict input to maximum length based on verification method
    if (numericInput.length > maxCodeLength) {
      numericInput = numericInput.slice(0, maxCodeLength);
    }

    // Update the input field and button state
    event.target.value = numericInput;

    if (verificationSubmitButton) {
      verificationSubmitButton.disabled = numericInput.length !== maxCodeLength;
    }
  }

  /**
   * Handle email verification code form submission
   * Updated for V2 API with proper response handling
   */
  async handleEmailVerificationSubmit(event) {
    event.preventDefault();

    const emailFromSession = getSessionStorageItem(SESSION_KEY.PROFILE)?.email;

    if (!emailFromSession || !this.elements.verificationSubmitButton || !this.elements.verificationCodeInput) {
      console.error('Required data or elements not found');
      return;
    }

    let isRedirecting = false;
    const code = this.elements.verificationCodeInput.value.trim();
    if (!code) {
      showToastMessage('Please enter the verification code.');
      return;
    }

    setButtonLoading(this.elements.verificationSubmitButton, true);
    const collector = new SystemInfoCollector();
    const deviceInfo = await collector.getInfo();

    // Build request body with dynamic return URL
    const returnURL = window.location.origin + (this.isCheckout && this.returnTo ? this.returnTo : PAGE_URLS.ACCOUNT);

    const requestBody = {
      email: emailFromSession,
      type: this.determineVerificationType() === 'sms' ? 'PHONE' : 'EMAIL',
      code: code,
      returnURL: returnURL,
      deviceInfo: deviceInfo,
    };

    // Make API call
    const response = await this.submitVerificationCode(requestBody);
    const profileData = await response.json().catch(() => null);

    if (response.ok) {
      setSessionStorageItem(SESSION_KEY.PROFILE, profileData || {});

      // Handle redirect based on multipass token presence
      isRedirecting = true;
      this.handleVerificationSuccess(profileData || {});
      return;
    }

    this.handleError(response.status);
    try {
      await this.executeWithErrorHandling(operation, this.elements.verificationSubmitButton);
    } catch (error) {
      // Only remove loading state if we're not redirecting
      if (!isRedirecting) {
        setButtonLoading(this.elements.verificationSubmitButton, false);
      }
    }
  }

  /**
   * Handle successful verification and redirect
   * @param {Object} profileData - Profile data from successful verification
   */
  handleVerificationSuccess(profileData) {
    if (!profileData.multipassToken) {
      setAuthFlowState(AUTH_FLOW_STATES.REGISTRATION_ALLOWED);
      setSessionStorageItem(SESSION_KEY.PROFILE, profileData);

      window.location.replace(PAGE_URLS.SHOPIFY_REGISTER);
    } else {
      clearAuthFlowState();
      deleteSessionStorageItem(SESSION_KEY.PROFILE);
      deleteSessionStorageItem(SESSION_KEY.CHECKOUT);
      deleteSessionStorageItem(SESSION_KEY.RETURN_TO);
      deleteSessionStorageItem(SESSION_KEY.VERIFICATION_METHOD);

      window.location.replace(PAGE_URLS.MULTIPASS_LOGIN(profileData.multipassToken));
    }
  }

  // Verify profile email via API
  async verifyProfileEmail(params) {
    const verifyProfilePayload = STYKU_API.VERIFY_PROFILE_EMAIL;
    verifyProfilePayload.body = JSON.stringify(params);

    return await fetchData(verifyProfilePayload);
  }

  // Submit verification code via API
  async submitVerificationCode(requestBody) {
    const verifyProfilePayload = STYKU_API.EMAIL_VERIFICATION_CODE;
    verifyProfilePayload.body = JSON.stringify(requestBody);

    return await fetchData(verifyProfilePayload);
  }

  // Resend the verification code to the user's email or phone
  async resendVerificationCode(params) {
    const verifyProfilePayload = STYKU_API.SEND_EMAIL_VERIFICATION_CODE;
    verifyProfilePayload.body = JSON.stringify(params);

    const response = await fetchData(verifyProfilePayload);

    // Reset timer only on successful resend
    if (response.ok) this.resetDynamicTimer();

    return response;
  }

  /**
   * Generic API error handler for backward compatibility
   * @param {number} status - HTTP status code
   */
  handleError(status) {
    switch (status) {
      case ERROR_CODES.BAD_REQUEST:
        showToastMessage(this.config.exceptionMessage.badRequest);
        break;
      case ERROR_CODES.GONE:
        showToastMessage(this.config.exceptionMessage.verificationCodeExpired);
        break;
      case ERROR_CODES.EARLY_REQUEST:
        showToastMessage(this.config.exceptionMessage.earlyRequest);
        break;
      case ERROR_CODES.MANY_REQUESTS:
        showToastMessage(this.config.exceptionMessage.tooManyRequests);
        break;
      case ERROR_CODES.SERVER_ERROR:
        showToastMessage(this.config.exceptionMessage.somethingWentWrong);
        break;
      case VERIFICATION_CODE.INVALID_VERIFICATION_CODE:
        showToastMessage(this.config.exceptionMessage.invalidVerificationCode);
        break;
      case VERIFICATION_CODE.INVALID_EMAIL:
        showToastMessage(this.config.exceptionMessage.invalidEmail);
        break;
      case VERIFICATION_CODE.UNABLE_TO_SEND_EMAIL:
        showToastMessage(this.config.exceptionMessage.unableToSendEmail);
        break;
      case VERIFICATION_CODE.PROFILE.NOT_VERIFIED:
        showToastMessage(this.config.exceptionMessage.profileNotVerified);
        break;
      case VERIFICATION_CODE.INVALID_PHONE_NUMBER:
        showToastMessage(this.config.exceptionMessage.invalidPhoneNumber);
        break;
      case VERIFICATION_CODE.PROFILE.DEMO_OR_APP_TESTER:
        showToastMessage(this.config.exceptionMessage.demoAccountEmail);
        break;
      default:
        showToastMessage(this.config.exceptionMessage.somethingWentWrong);
        break;
    }
  }
}

document.addEventListener('DOMContentLoaded', function () {
  const verifyProfile = new VerifyProfileManager();
  verifyProfile.init();
});
